import React, { useState, useEffect } from "react";
import {
    FaRocket, FaPlus, FaCode, FaFileAlt, FaNetworkWired, FaLink,
    FaChartBar, FaSearch, FaClipboardCheck, FaMagic, FaStar,
    FaHeart, FaCoffee, FaUsers, FaLightbulb, FaTrophy,
    FaGift, FaThumbsUp, FaPlay, FaDownload, FaUpload, FaEye,
    FaInfoCircle
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import NodementedOnboardingModal from './ui/NodementedOnboardingModal';
import FeatureManager from './FeatureManager';
import OnboardingGuide from './ui/OnboardingGuide';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import BackToTop from './ui/BackToTop';
import logo from '../assets/nodemented-logo.png';
import UserDashboard from './UserDashboard';
import LicenseStatusWidget from './LicenseStatusWidget';
import BillingPage from './BillingPage';
import CGU from './CGU';
import Settings from './Settings';
import DMBTTrigger from './ui/DMBTTrigger';
import DMBTGuide from './DMBTGuide';
import '../styles/modern-dashboard.css';
import CookieConsentBanner from './CookieConsentBanner';
import ToolMenu from './ToolMenu';
import Playbooks from './Playbooks';
import PlaybooksToggleButton from './PlaybooksToggleButton';
import MissionControl from './MissionControl';
import { isFeatureEnabled, FEATURES } from '../config/featureFlags';
const API_BASE_URL = "http://localhost:3001";

const Dashboard = ({ user, onLogout }) => {
    const { t } = useTranslation();
    const [showPlaybooksInfo, setShowPlaybooksInfo] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [nodeStats, setNodeStats] = useState(null);
    const [hasNodes, setHasNodes] = useState(false);
    const [showFeatureManager, setShowFeatureManager] = useState(false);
    const [selectedPersona, setSelectedPersona] = useState(null);
    const [openSafeChangeFlow, setOpenSafeChangeFlow] = useState(false);
    const [showUserDashboard, setShowUserDashboard] = useState(false);
    const [showBillingPage, setShowBillingPage] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showCGU, setShowCGU] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showDMBTGuide, setShowDMBTGuide] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [motivationalMessage, setMotivationalMessage] = useState('');
    const [showPlaybooks, setShowPlaybooks] = useState(false);

    // Modern onboarding: display only on first visit
    const [onboardingDone, setOnboardingDone] = useState(() => localStorage.getItem('nodementedOnboardingDone') === 'true');

    // Get motivational messages from translations
    const getRandomMotivationalMessage = () => {
        const messages = t('motivationalMessages', { returnObjects: true });
        if (Array.isArray(messages) && messages.length > 0) {
            return messages[Math.floor(Math.random() * messages.length)];
        }
        return "🚀 Let's build something amazing!"; // Fallback
    };

    // Check if it's first visit and load statistics
    useEffect(() => {
        const loadDashboard = async () => {
            setIsLoading(true);

            // Check if we should show FeatureManager after refresh
            const shouldShowFeatureManager = localStorage.getItem('showFeatureManagerAfterRefresh');
            if (shouldShowFeatureManager === 'true') {
                localStorage.removeItem('showFeatureManagerAfterRefresh');
                setShowFeatureManager(true);
            }

            // Debug: Log user info
            //console.log('🔍 [Dashboard] User object:', user);
            //console.log('🔍 [Dashboard] User role:', user?.role);
            //console.log('🔍 [Dashboard] Should show admin button:', (user?.role === 'org-admin' || user?.role === 'admin'));

            // Random motivational message (localized list)
            const randomMessage = getRandomMotivationalMessage();
            setMotivationalMessage(randomMessage);
            // Modern onboarding: first visit only (don't force on every reload)
            // L'onboarding s'affiche seulement si onboardingDone === false ET showOnboarding === false
            if (!onboardingDone && !showOnboarding) {
                setShowOnboarding(true);
            }
            try {
                const response = await apiClient.get('/api/user/stats');
                const statsData = {
                    ...response.data.nodes,
                    relations: response.data.relations,
                    testsCreated: response.data.meta?.testsCreated,
                    testsValidated: response.data.meta?.testsValidated
                };
                setNodeStats(statsData);
                setHasNodes(response.data.nodes.total > 0);
            } catch (error) {
                if (handleAuthError(error)) return;
                console.error(t('dashboard.errors.loadingStats'), error);
                setNodeStats({
                    total: 0,
                    features: 0,
                    apis: 0,
                    services: 0,
                    relations: { total: 0 },
                    testCoverage: 0,
                    risksCount: 0
                });
            } finally {
                setIsLoading(false);
            }
        };
        loadDashboard();
    }, [onboardingDone]);

    const handleOnboardingComplete = () => {
        setShowOnboarding(false);
        setOnboardingDone(true);
        localStorage.setItem('nodementedOnboardingDone', 'true');
    };
    const handleOnboardingSkip = () => {
        setShowOnboarding(false);
        setOnboardingDone(true);
        localStorage.setItem('nodementedOnboardingDone', 'true');
    };
    const handleOnboardingHelp = () => {
        setShowOnboarding(true);
    };

    // Gestion de la modal DMBT Guide
    const handleOpenDMBTGuide = () => {
        setShowDMBTGuide(true);
    };

    const handleCloseDMBTGuide = () => {
        setShowDMBTGuide(false);
    };

    const handleShowFeatureManager = (section = null, persona = null) => {
        if (persona) setSelectedPersona(persona);
        setShowFeatureManager(true);
        if (section) {
            setTimeout(() => {
                document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
            }, 300);
        }
    };

    const handleSafeChangeFlow = (persona = null) => {
        if (persona) setSelectedPersona(persona);
        setOpenSafeChangeFlow(true);
        setShowFeatureManager(true);
    };

    // Si l'utilisateur veut aller au FeatureManager complet
    if (showFeatureManager) {
        return (
            <FeatureManager
                onBackToDashboard={() => { setShowFeatureManager(false); setSelectedPersona(null); setOpenSafeChangeFlow(false); }}
                user={user}
                onLogout={onLogout}
                persona={selectedPersona}
                openSafeChangeFlow={openSafeChangeFlow}
                onSafeChangeFlowClosed={() => setOpenSafeChangeFlow(false)}
            />
        );
    }

    // Si l'utilisateur veut voir son profil/abonnement
    if (showUserDashboard) {
        return (
            <UserDashboard
                user={user}
                onLogout={onLogout}
                onBackToDashboard={() => setShowUserDashboard(false)}
            />
        );
    }

    // Si l'utilisateur veut voir la page de billing
    if (showBillingPage) {
        return (
            <BillingPage
                user={user}
                onBackToDashboard={() => setShowBillingPage(false)}
            />
        );
    }

    // Si l'utilisateur veut voir les CGU
    if (showCGU) {
        return (
            <div className="cgu-fullscreen">
                <div className="cgu-header-bar">
                    <button
                        onClick={() => setShowCGU(false)}
                        className="btn-back-dashboard"
                    >
                        ← {t('dashboard.welcome')}
                    </button>
                </div>
                <CGU
                    showButtons={false}
                />
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="dashboard-loading">
                <div className="loading-animation">
                    <div className="loading-spinner">
                        <FaStar />
                    </div>
                    <h2>✨ {t('app.loading')}</h2>
                    <p>{t('common.processing')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="modern-dashboard">
            {/* Onboarding ultra-moderne Nodemented */}
            <NodementedOnboardingModal
                isOpen={showOnboarding}
                onClose={handleOnboardingSkip}
                onComplete={handleOnboardingComplete}
                onSkip={handleOnboardingSkip}
                reactivable={true}
            />
            {/* Bouton d'aide/tour pour relancer l'onboarding */}
            {onboardingDone && (
                <button
                    className="onboarding-help-btn"
                    style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, background: 'linear-gradient(90deg, #667EEA 0%, #764BA2 100%)', color: '#fff', border: 'none', borderRadius: '50%', width: 56, height: 56, boxShadow: '0 2px 8px rgba(76,81,255,0.12)', fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    aria-label={t('dashboard.onboardingHelpAria')}
                    onClick={handleOnboardingHelp}
                >
                    ?
                </button>
            )}

            {/* Header moderne */}
            <header className="modern-header">
                <div className="header-brand">
                    <div className="brand-logo">
                        <img src={logo} alt={t('dashboard.brand.title')} />
                        <span className="brand-pulse"></span>
                    </div>
                    <div className="brand-text">
                        <h1>{t('dashboard.brand.title')}</h1>
                        <p>{t('dashboard.brand.subtitle')}</p>
                    </div>
                </div>

                <div className="header-actions">

                    <button
                        onClick={() => setShowFeatureManager(true)}
                        className="lab-btn002"
                    >
                        {t('dashboard.enterLab')}
                    </button>

                    {(user?.role === 'org-admin' || user?.role === 'admin') && (
                        <button
                            className="action-btn admin-btn"
                            onClick={() => window.location.href = '/admin-dashboard'}
                        >
                            🏢 {t('dashboard.admin')}
                        </button>
                    )}

                    <div className="user-menu-container">
                        <button
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className="user-profile-btn"
                        >
                            <div className="user-avatar">
                                {user?.firstName?.[0]?.toUpperCase()}{user?.lastName?.[0]?.toUpperCase()}
                            </div>
                            <span className="user-name">{user?.firstName}</span>
                            <span className="dropdown-icon">▼</span>
                        </button>

                        {showUserMenu && (
                            <div className="dashboardDropUser user-dropdown">
                                <button
                                    onClick={() => {
                                        setShowUserDashboard(true);
                                        setShowUserMenu(false);
                                    }}
                                    className="dropdown-item"
                                >
                                    👤 {t('profile.title')}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowSettings(true);
                                        setShowUserMenu(false);
                                    }}
                                    className="dropdown-item"
                                >
                                    ⚙️ {t('settings.title')}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowBillingPage(true);
                                        setShowUserMenu(false);
                                    }}
                                    className="dropdown-item"
                                >
                                    💳 {t('billingPage.title')}
                                </button>
                                {user?.role !== 'superadmin' && (
                                    <button
                                        onClick={() => {
                                            setShowCGU(true);
                                            setShowUserMenu(false);
                                        }}
                                        className="dropdown-item"
                                    >
                                        📜 {t('cgu.title')}
                                    </button>
                                )}
                                <div className="dropdown-divider"></div>
                                <button
                                    onClick={() => {
                                        setShowUserMenu(false);
                                        onLogout();
                                    }}
                                    className="dropdown-item logout"
                                >
                                    🚪 {t('auth.logout')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Cookie consent banner — shown on dashboard pages */}
            <CookieConsentBanner policyUrl="/policy/cookies" />

            <main className="modern-content">
                {hasNodes ? (
                    /* Dashboard pour utilisateurs avec données */
                    isFeatureEnabled(FEATURES.MISSION_CONTROL) ? (
                    /* 🚀 Mission Control — Dashboard orienté valeur (feature flag) */
                    <div className="dashboard-mc-layout">
                        <MissionControl
                            user={user}
                            nodeStats={nodeStats}
                            onNavigateToLab={() => { setSelectedPersona(null); handleShowFeatureManager('section-graph', null); }}
                            onNavigateToSection={(section, persona) => handleShowFeatureManager(section, persona)}
                            onSafeChangeFlow={handleSafeChangeFlow}
                            motivationalMessage={motivationalMessage}
                            onOpenDMBTGuide={handleOpenDMBTGuide}
                            licenseWidget={<LicenseStatusWidget user={user} />}
                        />
                    </div>
                    ) : (
                    /* Legacy Dashboard — original stats view (fallback) */
                    <div className="dashboard-with-data">
                        {/* Hero section simplifié avec stats visuelles */}
                        <section className="hero-stats">
                            <div className="welcome-message">
                                <h2>{t('dashboard.greeting', { name: user?.firstName })} 👋</h2>
                                <p className="motivational">{motivationalMessage}</p>
                            </div>


                            {/* Affichage conditionnel des fiches outils */}
                            {showPlaybooks && (
                                <Playbooks
                                    isOpen={showPlaybooks}
                                    onClose={() => setShowPlaybooks(false)}
                                    explainer={
                                        <div style={{ background: 'rgba(102,126,234,0.08)', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px', fontSize: '1rem', color: '#444', boxShadow: '0 2px 8px rgba(76,81,255,0.06)' }}>
                                            <strong>📚 Playbooks :</strong> Des guides concrets pour chaque étape du projet, rédigés par des experts. Utilisez-les pour gagner du temps et éviter les pièges courants !
                                        </div>
                                    }
                                />
                            )}
                            <div className="dmbt-guide-section">
                                {/* Widget de statut de licence intégré dans le dashboard principal */}
                                <LicenseStatusWidget user={user} />
                                {/* PlaybooksToggleButton avec bouton info intégré */}
                              
                                <DMBTTrigger onClick={handleOpenDMBTGuide} />
                                  <PlaybooksToggleButton
                                    showPlaybooks={showPlaybooks}
                                    onToggle={() => setShowPlaybooks(!showPlaybooks)}
                                />
                            </div>

                            <div className="stats-grid">
                                <div className="stat-card primary">
                                    <div className="stat-content">
                                        <span className="stat-number">{nodeStats?.total || 0}</span>
                                        <span className="stat-label">{t('dashboard.stats.nodes')}</span>
                                    </div>
                                    <FaNetworkWired className="stat-icon" />
                                </div>

                                <div className="stat-card secondary">
                                    <div className="stat-content">
                                        <span className="stat-number">{nodeStats?.relationsCount ?? nodeStats?.relations ?? 0}</span>
                                        <span className="stat-label">{t('dashboard.stats.relations')}</span>
                                    </div>
                                    <FaLink className="stat-icon" />
                                </div>

                                <div className="stat-card accent">
                                    <div className="stat-content">
                                        <span className="stat-number">{nodeStats?.testCoverage || 0}%</span>
                                        <span className="stat-label">{t('dashboard.stats.tests')}</span>
                                    </div>
                                    <FaClipboardCheck className="stat-icon" />
                                </div>
                            </div>
                        </section>

                        {/* Lab access card */}
                        <section className="lab-access">
                            <div className="lab-card">
                                <div className="lab-content">
                                    <FaRocket className="lab-icon" />
                                    <div className="lab-text">
                                        <h3>{t('dashboard.lab.title')}</h3>
                                        <p>{t('dashboard.lab.subtitle')}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowFeatureManager(true)}
                                    className="lab-btn"
                                >
                                    {t('dashboard.enterLab')}
                                </button>
                            </div>
                        </section>


                        {/* Actions rapides simplifiées */}
                        <section className="quick-actions">
                            <h3>{t('dashboard.quickActions.title')}</h3>
                            <div className="action-grid">
                                <button
                                    className="quick-action-btn create"
                                    onClick={() => handleShowFeatureManager('section-quick-create')}
                                >
                                    <FaPlus className="action-icon" />
                                    <div className="action-content">
                                        <h4>{t('dashboard.quickActions.create.title')}</h4>
                                        <p>{t('dashboard.quickActions.create.desc')}</p>
                                    </div>
                                </button>

                                <button
                                    className="quick-action-btn explore"
                                    onClick={() => handleShowFeatureManager('section-graph')}
                                >
                                    <FaSearch className="action-icon" />
                                    <div className="action-content">
                                        <h4>{t('dashboard.quickActions.explore.title')}</h4>
                                        <p>{t('dashboard.quickActions.explore.desc')}</p>
                                    </div>
                                </button>

                                <button
                                    className="quick-action-btn analyze"
                                    onClick={() => handleShowFeatureManager('section-analysis')}
                                >
                                    <FaChartBar className="action-icon" />
                                    <div className="action-content">
                                        <h4>{t('dashboard.quickActions.analyze.title')}</h4>
                                        <p>{t('dashboard.quickActions.analyze.desc')}</p>
                                    </div>
                                </button>

                                <button
                                    className="quick-action-btn import"
                                    onClick={() => handleShowFeatureManager('section-import-code')}
                                >
                                    <FaCode className="action-icon" />
                                    <div className="action-content">
                                        <h4>{t('dashboard.quickActions.import.title')}</h4>
                                        <p>{t('dashboard.quickActions.import.desc')}</p>
                                    </div>
                                </button>
                            </div>
                        </section>


                    </div>
                    )
                ) : (
                    /* Dashboard pour nouveaux utilisateurs */
                    <div className="dashboard-first-time">
                        <div className="welcome-hero">
                            <div className="hero-content">
                                <div className="hero-animation">
                                    <div className="floating-icons">
                                        <FaNetworkWired className="icon-1" />
                                        <FaLink className="icon-2" />
                                        <FaLightbulb className="icon-3" />
                                    </div>
                                </div>
                                <h1>{t('dashboard.firstTime.title')}</h1>
                                <p className="hero-subtitle">
                                    {t('dashboard.firstTime.subtitle')}
                                </p>
                                <div className="motivation-badges">
                                    <span className="badge fun">🚀 {t('dashboard.badges.fun')}</span>
                                    <span className="badge powerful">💪 {t('dashboard.badges.powerful')}</span>
                                    <span className="badge collaborative">🤝 {t('dashboard.badges.collaborative')}</span>
                                </div>
                            </div>
                        </div>

                        <div className="getting-started">
                            <h2>{t('dashboard.firstTime.howStartTitle')}</h2>
                            <div className="starter-options">
                                <button
                                    className="starter-card primary"
                                    onClick={() => handleShowFeatureManager('section-quick-create')}
                                >
                                    <div className="starter-icon">
                                        <FaPlus />
                                    </div>
                                    <h3>{t('dashboard.firstTime.starter.create.title')}</h3>
                                    <p>{t('dashboard.firstTime.starter.create.desc')}</p>
                                    <div className="starter-badge">{t('dashboard.firstTime.starter.create.badge')}</div>
                                </button>

                                <button
                                    className="starter-card secondary"
                                    onClick={() => handleShowFeatureManager('section-import-code')}
                                >
                                    <div className="starter-icon">
                                        <FaCode />
                                    </div>
                                    <h3>{t('dashboard.firstTime.starter.importCode.title')}</h3>
                                    <p>{t('dashboard.firstTime.starter.importCode.desc')}</p>
                                    <div className="starter-badge">{t('dashboard.firstTime.starter.importCode.badge')}</div>
                                </button>

                                <button
                                    className="starter-card tertiary"
                                    onClick={() => handleShowFeatureManager('section-import-doc')}
                                >
                                    <div className="starter-icon">
                                        <FaFileAlt />
                                    </div>
                                    <h3>{t('dashboard.firstTime.starter.importDoc.title')}</h3>
                                    <p>{t('dashboard.firstTime.starter.importDoc.desc')}</p>
                                    <div className="starter-badge">{t('dashboard.firstTime.starter.importDoc.badge')}</div>
                                </button>

                                <button
                                    className="starter-card explore"
                                    onClick={() => setShowFeatureManager(true)}
                                >
                                    <div className="starter-icon">
                                        <FaRocket />
                                    </div>
                                    <h3>{t('dashboard.firstTime.starter.exploreLab.title')}</h3>
                                    <p>{t('dashboard.firstTime.starter.exploreLab.desc')}</p>
                                    <div className="starter-badge">{t('dashboard.firstTime.starter.exploreLab.badge')}</div>
                                </button>
                            </div>
                        </div>

                        <div className="value-props">
                            <h3>{t('dashboard.valueProps.title')}</h3>
                            <div className="value-grid">
                                <div className="value-item">
                                    <FaUsers />
                                    <h4>{t('dashboard.valueProps.collaboration.title')}</h4>
                                    <p>{t('dashboard.valueProps.collaboration.desc')}</p>
                                </div>
                                <div className="value-item">
                                    <FaMagic />
                                    <h4>{t('dashboard.valueProps.ai.title')}</h4>
                                    <p>{t('dashboard.valueProps.ai.desc')}</p>
                                </div>
                                <div className="value-item">
                                    <FaChartBar />
                                    <h4>{t('dashboard.valueProps.impact.title')}</h4>
                                    <p>{t('dashboard.valueProps.impact.desc')}</p>
                                </div>
                                <div className="value-item">
                                    <FaCoffee />
                                    <h4>{t('dashboard.valueProps.simplicity.title')}</h4>
                                    <p>{t('dashboard.valueProps.simplicity.desc')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <BackToTop />
            </main>

            {/* Modal pour le guide DMBT */}
            <DMBTGuide
                isOpen={showDMBTGuide}
                onClose={handleCloseDMBTGuide}
            />

            {/* Settings Modal */}
            {showSettings && (
                <Settings
                    user={user}
                    onClose={() => setShowSettings(false)}
                />
            )}
        </div>
    );
}

export default Dashboard;